import React from 'react'
import { Button } from '../ui/Button'
import { useAuth } from '../AuthProvider'
import { useI18n } from '../../i18n'
import { getActiveOrgId } from '../../utils/orgUtils'
import { fetchCompleteness, type CompletenessChecklist } from '../../services/informationSystemPhase13Api'

interface Props {
  systemId: string
}

export default function InformationSystemCompletenessChecklist({ systemId }: Props) {
  const { t } = useI18n()
  const { token } = useAuth()
  const orgId = getActiveOrgId()
  const [data, setData] = React.useState<CompletenessChecklist | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const load = React.useCallback(async () => {
    if (!token) return
    setLoading(true)
    try {
      const row = await fetchCompleteness(token, systemId, orgId)
      setData(row)
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('assets.completeness.error.load', 'Failed to load checklist'))
    } finally {
      setLoading(false)
    }
  }, [token, systemId, orgId, t])

  React.useEffect(() => {
    load()
  }, [load])

  if (loading) return <p className="text-xs text-gray-500">{t('common.loading')}</p>
  if (error) {
    return (
      <p className="text-xs text-red-600">
        {error}{' '}
        <Button type="button" size="small" color="outline" onClick={() => load()}>
          {t('common.retry', 'Retry')}
        </Button>
      </p>
    )
  }
  if (!data) return null

  return (
    <div className="border rounded p-3 bg-slate-50">
      <div className="flex justify-between items-center mb-2">
        <h4 className="text-sm font-medium">{t('assets.completeness.title', 'Completeness checklist')}</h4>
        <span className="text-xs text-gray-600">
          {data.completeCount}/{data.totalCount}
        </span>
      </div>
      <p className="text-xs text-gray-500 mb-2">
        {t('assets.completeness.hint', 'Operational checklist only — not a compliance score.')}
      </p>
      <ul className="space-y-1">
        {data.items
          .filter((i) => !i.optional)
          .map((item) => (
            <li key={item.id} className="flex items-start gap-2 text-sm">
              <span className={item.complete ? 'text-green-600' : 'text-gray-400'}>{item.complete ? '✓' : '○'}</span>
              <span className={item.complete ? 'text-gray-700' : 'text-gray-600'}>
                {t(`assets.completeness.items.${item.id}`, item.message)}
              </span>
            </li>
          ))}
      </ul>
    </div>
  )
}
